import {
  Box,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Input,
  Tbody,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as lodash from 'lodash';
import React, { useState, useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import {
  FormulaEngine,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { ReportingEditIntermediateFieldModal } from './ReportingEditIntermediateField';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from 'packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const { cloneDeep } = lodash;

const createIntermediateFieldShell = (
  idx: number,
): CreditAgreement.Config.Reporting.Inputs.IntermediateField => {
  return {
    field: `[ fieldName${idx} ]`,
    config: {
      formula: '[ PlaceHolder ]',
      type: 'formula',
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    },
  };
};

type ReportingIntermediateFieldProps = {
  intermediateField: CreditAgreement.Config.Reporting.Inputs.IntermediateField;
  wipConfig?: WipConfig;
  configPropertyKey: string;
  basePropertyKey: string;
  fullPropertyKey: string;
  idx: number;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  id: string;
};
const ReportingIntermediateField = ({
  wipConfig,
  intermediateField,
  basePropertyKey,
  configPropertyKey,
  fullPropertyKey,
  idx,
  moveRow,
  isDragDisabled,
  id,
}: ReportingIntermediateFieldProps) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const deleteIntermediateField = (wc: WipConfig): void => {
    const fields = getIn(wc.formProps.values, basePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isReportingInputsIntermediateFields(
        fields,
      ),
      `Retrieved non Intermediate Fields type for ${basePropertyKey}`,
    );

    wc.formProps.setFieldValue(
      basePropertyKey,
      fields.filter((_, varIdx) => varIdx !== idx),
    );
  };

  const onSave = useCallback(
    (
      field: CreditAgreement.Config.Reporting.Inputs.IntermediateField,
      onSaveIdx: number,
    ): void => {
      if (!wipConfig) {
        return;
      }

      const config = getIn(wipConfig.formProps.values, configPropertyKey);
      finleyAssert(
        finleyTypeguards.creditAgreement.isReportingInputsConfig(config),
        `Retrieve non Inputs Config type for ${configPropertyKey}`,
      );
      const configCopy = cloneDeep(config);

      const sanitizedField = field;
      // FormulaEngine expects formulas without leading "=", so trimming if present
      if (field.config.formula.startsWith('=')) {
        sanitizedField.config.formula = field.config.formula.slice(1);
      }

      configCopy.intermediateFields = configCopy.intermediateFields.map(
        (configIntermediateField, index) =>
          index === onSaveIdx ? sanitizedField : configIntermediateField,
      );

      // Setting "queryResults" as empty array since don't know them at this point
      // as inputs, like "DataAccessors", are async
      const intermediateFieldErrors = reportingValidate.input.validateIntermediateFields(
        { intermediateFields: configCopy.intermediateFields, queryResults: [] },
      );
      if (intermediateFieldErrors.length > 0) {
        toast({
          title: 'Intermediate Field Error',
          description: intermediateFieldErrors.join(', '),
          status: 'error',
          position: 'top',
          isClosable: true,
        });
        return;
      }

      wipConfig.formProps.setFieldValue(fullPropertyKey, field);
    },
    [wipConfig, fullPropertyKey, configPropertyKey, toast],
  );

  return (
    <TrDraggableHandle
      index={idx}
      id={id}
      acceptedType="intermediateField"
      moveRow={moveRow}
      isDragDisabled={isDragDisabled}
    >
      <Td w="40">{intermediateField.field}</Td>
      <Td w="12">{intermediateField.config.type}</Td>
      <Td w="65%">
        <Input
          p="0"
          value={intermediateField.config.formula}
          readOnly
          border="none"
        />
      </Td>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && isDragDisabled && (
        <Td>
          <ReportingEditIntermediateFieldModal
            isOpen={isOpen}
            onCancel={() => setIsOpen(false)}
            onSave={onSave}
            intermediateField={intermediateField}
            idx={idx}
          />
          <EditIconButton
            ariaLabel="edit field"
            label="Edit Field"
            onClick={() => {
              setIsOpen(true);
            }}
          />
          <DeleteIconButton
            ariaLabel="delete field"
            label="Delete Field"
            onClick={() => {
              deleteIntermediateField(wipConfig);
            }}
          />
        </Td>
      )}
    </TrDraggableHandle>
  );
};

type ReportingIntermediateFieldsProps = {
  intermediateFields: CreditAgreement.Config.Reporting.Inputs.IntermediateField[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  configPropertyKey: string;
};
export const ReportingIntermediateFields = ({
  wipConfig,
  basePropertyKey,
  configPropertyKey,
  intermediateFields,
}: ReportingIntermediateFieldsProps) => {
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.Config.Reporting.Inputs.IntermediateField>(
    { comparisonKey: 'field' },
  );
  const [isOrderLocked] = useOrderLockedState();
  const [intermediateFieldsState] = useRowState(intermediateFields);

  const onSaveOrder = useCallback((): void => {
    wipConfig?.formProps.setFieldValue(
      basePropertyKey,
      intermediateFieldsState,
    );
  }, [basePropertyKey, intermediateFieldsState, wipConfig]);

  const addIntermediateField = (wc: WipConfig): void => {
    const fields = cloneDeep(getIn(wc.formProps.values, basePropertyKey));
    const field = createIntermediateFieldShell(fields.length);
    fields.push(field);
    wc.formProps.setFieldValue(basePropertyKey, fields);
  };

  return (
    <Box id="reporting-intermediate-field-container">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Intermediate Fields
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            {isOrderLocked && (
              <AddIconButton
                ariaLabel="add intermeidate field"
                label="Add Intermediate Field"
                onClick={() => {
                  addIntermediateField(wipConfig);
                }}
              />
            )}
            <LockOrderButton onSaveOrder={onSaveOrder} />
            {!isOrderLocked && <RestoreOrderButton />}
          </>
        )}
      </Flex>

      {wipConfig?.isMutateAllowed(basePropertyKey) && <SaveOrderCallout />}

      {intermediateFields.length > 0 ? (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              {wipConfig && !isOrderLocked && <Th />}
              <Th>Field Name</Th>
              <Th>Type</Th>
              <Th>Formula</Th>
              {wipConfig && isOrderLocked && <Th>Edit/Delete</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {intermediateFieldsState.map((intermediateField, idx) => {
              const id = getIterationKey(intermediateField);
              return (
                <ReportingIntermediateField
                  key={id}
                  configPropertyKey={configPropertyKey}
                  intermediateField={intermediateField}
                  wipConfig={wipConfig}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={`${basePropertyKey}[${idx}]`}
                  idx={idx}
                  moveRow={moveRow}
                  isDragDisabled={isOrderLocked}
                  id={id}
                />
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no intermediate fields.
        </Text>
      )}
    </Box>
  );
};
